import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n";
import heroBackground from "@/assets/hero-background.jpg";

export function HeroSection() {
  const { t } = useLanguage();

  return (
    <section id="accueil" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <img
        src={heroBackground}
        alt=""
        fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Dark Overlay for better text readability */}
      <div className="absolute inset-0 bg-noir/60" />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-noir/40 via-transparent to-noir/80" />
      
      {/* CSS-animated sparkles (no JS overhead) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="hero-sparkle" style={{ left: "10%", top: "20%" }} />
        <div className="hero-sparkle" style={{ left: "85%", top: "30%", animationDelay: "0.5s" }} />
        <div className="hero-sparkle" style={{ left: "20%", top: "70%", animationDelay: "1s" }} />
        <div className="hero-sparkle" style={{ left: "75%", top: "60%", animationDelay: "1.5s" }} />
        <div className="hero-sparkle" style={{ left: "50%", top: "15%", animationDelay: "2s" }} />
        <div className="hero-sparkle" style={{ left: "30%", top: "45%", animationDelay: "2.5s" }} />
        <div className="hero-sparkle" style={{ left: "90%", top: "75%", animationDelay: "3s" }} />
        <div className="hero-sparkle" style={{ left: "65%", top: "40%", animationDelay: "0.8s" }} />

        {/* CSS-animated dots */}
        <div className="hero-dot" style={{ left: "8%", top: "25%" }} />
        <div className="hero-dot" style={{ left: "92%", top: "35%", animationDelay: "0.6s" }} />
        <div className="hero-dot" style={{ left: "42%", top: "12%", animationDelay: "1.2s" }} />
        <div className="hero-dot" style={{ left: "58%", top: "88%", animationDelay: "1.8s" }} />
        <div className="hero-dot" style={{ left: "72%", top: "22%", animationDelay: "2.4s" }} />
      </div>
      
      {/* CSS-animated glowing orbs */}
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />
      
      <div className="container relative z-10 py-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo Animation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mb-8"
          >
            <span className="text-5xl md:text-7xl font-cinzel font-bold tracking-wider">
              <span className="text-cream">VEECTOR</span>
              <span className="gold-shimmer">IA</span>
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-cream mb-6 leading-tight"
          >
            {t.hero.title}{" "}
            <span className="text-primary">{t.hero.titleHighlight}</span>
            <br />
            {t.hero.titleEnd}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-lg md:text-xl text-cream/80 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href="#contact">
              <Button size="lg" className="btn-gold gap-2 h-12 px-6">
                <Calendar className="w-5 h-5" />
                {t.hero.cta}
              </Button>
            </a>
            <a href="#prime-evolution">
              <Button
                size="lg"
                variant="outline"
                className="border-primary bg-transparent text-cream hover:bg-primary hover:text-noir gap-2 h-12 px-6"
              >
                {t.hero.discover}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hero-scroll-indicator">
          <div className="w-6 h-10 rounded-full border-2 border-cream/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-primary rounded-full hero-scroll-dot" />
          </div>
        </div>
      </div>
    </section>
  );
}
